import { useEffect, useMemo, useState } from 'react';
import { getSupabaseClient } from '../../data/supabase-client';
import {
  createBytesObjectUrl,
  createSupabasePhotoImageUrls,
  revokeObjectUrl,
  serverPhotoObjectPath,
  type PhotoVariant,
} from '../../data/photos/photo-images';
import type { CapturePhotoView } from '../../data/photos/photo-view';

/**
 * The URL for one variant of one photo — local bytes first, Storage second.
 *
 * **LOCAL WINS WHENEVER IT EXISTS, and that is a field decision.** A photo
 * Brandon has just taken is on the device; asking Storage for a copy of a
 * picture already in hand would make his own photos invisible in the basement
 * he took them in, and would spend metered bandwidth re-downloading bytes that
 * are metres away. The staged record is also the only source that exists
 * before the upload completes.
 *
 * Storage is used once the local bytes are gone — which happens only after the
 * server confirms the archive (D-028). The bucket is private, so that read is
 * a short-lived signed URL, issued to the owner and to nobody else.
 *
 * BLOB URLS ARE REVOKED. Each one pins its bytes in memory, and a Capture with
 * twelve photos would otherwise hold twelve full pictures alive for as long as
 * the tab lives.
 */
export function usePhotoImageUrl(view: CapturePhotoView, variant: PhotoVariant): string | null {
  const client = getSupabaseClient();
  const urls = useMemo(() => (client ? createSupabasePhotoImageUrls(client) : null), [client]);
  const [url, setUrl] = useState<string | null>(null);

  const staged = view.staged;
  const row = view.row;

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;

    const resolve = async (): Promise<string | null> => {
      if (staged) {
        const local =
          variant === 'archive'
            ? staged.archive
            : variant === 'display'
              ? staged.display
              : staged.thumbnail;
        created = createBytesObjectUrl(local.bytes, local.mime);
        return created;
      }
      if (row && urls) return await urls.signedUrl(serverPhotoObjectPath(row, variant));
      return null;
    };

    void resolve().then((next) => {
      if (!cancelled) setUrl(next);
    });

    return () => {
      cancelled = true;
      revokeObjectUrl(created);
    };
  }, [staged, row, urls, variant]);

  return url;
}
